import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Clock, Video, Phone, MapPin, Check, CalendarDays, ChevronLeft, UserCog } from "lucide-react";
import { toast } from "sonner";
import { RichTextView } from "@/components/common/RichTextEditor";

const PUB = `${process.env.REACT_APP_BACKEND_URL}/api/public/schedule`;
const TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;
const METHOD_ICON = { phone: Phone, teams: Video, in_person: MapPin, custom: Clock };

function fmtDate(d) { return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }); }
function iso(d) { return d.toISOString().slice(0, 10); }

export default function PublicSchedule() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [session, setSession] = useState(null); // {authenticated, role, name, page?, prefill?, interested_in?}
  const [err, setErr] = useState("");
  const [type, setType] = useState(null);
  const [date, setDate] = useState(new Date());
  const [slots, setSlots] = useState(null);
  const [slot, setSlot] = useState(null);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", mobile: "", city: "", state: "", occupation: "", interested_in: "", comments: "", company_website: "" });
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${PUB}/${slug}`).then((r) => {
      if (r.data.active === false) { setErr(r.data.message || "This scheduling page is currently unavailable."); return; }
      setPage(r.data);
      if (r.data.appointment_types?.length === 1) setType(r.data.appointment_types[0]);
    }).catch(() => setErr("Scheduling page not found."));
    // Auth-aware session (never 401s). Uses api wrapper so the signed-in token is included.
    api.get(`/public/schedule/${slug}/session`).then((r) => setSession(r.data)).catch(() => setSession({ authenticated: false }));
  }, [slug]);

  // Pre-populate a signed-in candidate's known details.
  useEffect(() => {
    if (session?.role === "candidate" && session.prefill) {
      setForm((f) => ({ ...f, ...session.prefill, interested_in: session.interested_in || f.interested_in }));
    }
  }, [session]);

  useEffect(() => {
    if (!type) return;
    setSlots(null); setSlot(null);
    axios.get(`${PUB}/${slug}/slots`, { params: { appointment_type_id: type.id, date: iso(date), tz: TZ } })
      .then((r) => setSlots(r.data.slots)).catch(() => setSlots([]));
  }, [type, date, slug]);

  const interestOptions = page?.interest_options || [];
  const fr = type?.field_rules || {};
  const interestRequired = fr.interested_in === "required";
  const interestHidden = fr.interested_in === "hidden";
  const phoneRequired = (fr.phone || "required") !== "optional";

  const book = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${PUB}/${slug}/book`, { appointment_type_id: type.id, start: slot.start, ...form });
      setDone(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not complete booking.");
      if (e?.response?.status === 409) { const rr = await axios.get(`${PUB}/${slug}/slots`, { params: { appointment_type_id: type.id, date: iso(date), tz: TZ } }); setSlots(rr.data.slots); setSlot(null); }
    } finally { setBusy(false); }
  };

  if (err) return <Shell><div className="text-center py-16" data-testid="schedule-unavailable"><CalendarDays className="w-10 h-10 mx-auto text-slate-300 mb-3" /><p className="text-slate-600">{err}</p></div></Shell>;
  if (!page) return <Shell><div className="py-20 text-center text-slate-400">Loading…</div></Shell>;

  if (done) return (
    <Shell>
      <div className="text-center py-10" data-testid="booking-confirmed">
        <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto mb-4"><Check className="w-7 h-7" /></div>
        <h2 className="text-2xl font-bold text-slate-800">Appointment Confirmed</h2>
        <p className="text-slate-500 mt-1">Your meeting with {page.branding?.display_name || page.name} is confirmed.</p>
        <div className="mt-6 mx-auto max-w-sm text-left rounded-xl border border-slate-200 p-5 space-y-2 text-sm">
          <Row label="With" value={page.branding?.display_name || page.name} />
          <Row label="Type" value={done.appointment_type} />
          <Row label="When" value={new Date(done.start).toLocaleString(undefined, { dateStyle: "full", timeStyle: "short" })} />
          <Row label="Timezone" value={TZ} />
          <Row label="Method" value={(done.method || "").replace("_", " ")} />
          {done.meeting_link
            ? <div className="flex gap-2"><span className="text-slate-400 w-20 shrink-0">Join</span><a href={done.meeting_link} target="_blank" rel="noreferrer" data-testid="confirm-teams-link" className="text-brand font-medium underline break-all">Microsoft Teams meeting link</a></div>
            : done.location ? <Row label="Location" value={done.location} /> : null}
        </div>
        {done.meeting_link ? (
          <a href={done.meeting_link} target="_blank" rel="noreferrer" data-testid="confirm-join-btn"
            className="inline-block mt-5 bg-[#0F2B5B] text-white text-sm font-semibold px-5 py-2.5 rounded-lg hover:opacity-90">Join Microsoft Teams Meeting</a>
        ) : null}
        {done.manage_token ? (
          <a href={`/schedule/manage/${done.manage_token}`} data-testid="confirm-manage-link"
            className="inline-block mt-6 text-sm font-medium text-brand hover:underline">Need to change it? Reschedule or cancel →</a>
        ) : null}
      </div>
    </Shell>
  );

  const brand = page.branding || {};
  return (
    <Shell>
      <AuthBanner session={session} />
      <div className="grid md:grid-cols-[300px_1fr] gap-8">
        <div className="md:border-r md:pr-8 border-slate-100">
          {brand.logo_url ? <img src={`${process.env.REACT_APP_BACKEND_URL}${brand.logo_url}`} alt={brand.company || "Agency logo"} className="h-12 w-auto max-w-[200px] object-contain mb-4" data-testid="schedule-agency-logo" /> : null}
          {brand.photo_url ? <img src={brand.photo_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${brand.photo_url}` : brand.photo_url} alt="" className="w-16 h-16 rounded-full object-cover mb-3" data-testid="schedule-host-photo" /> : null}
          {brand.company ? <p className="text-xs font-semibold uppercase tracking-wide text-slate-400" data-testid="schedule-company">{brand.company}</p> : null}
          <h1 className="text-xl font-bold text-slate-800 mt-1" data-testid="schedule-recruiter">{brand.display_name || page.name}</h1>
          {brand.title ? <p className="text-sm text-slate-500">{brand.title}</p> : null}
          <h2 className="text-base font-semibold text-slate-700 mt-4">{page.name}</h2>
          {page.intro ? <RichTextView html={page.intro} className="text-sm text-slate-500 mt-2 leading-relaxed" /> : null}
          {type ? <div className="mt-4 inline-flex items-center gap-1.5 text-sm text-slate-600"><Clock className="w-4 h-4" /> {type.duration_min} min</div> : null}
        </div>

        <div>
          {!type ? (
            <div className="space-y-2" data-testid="type-list">
              <p className="text-sm font-semibold text-slate-700 mb-2">Select an appointment type</p>
              {page.appointment_types.map((t) => { const I = METHOD_ICON[t.method] || Clock; return (
                <button key={t.id} data-testid={`type-${t.id}`} onClick={() => setType(t)}
                  className="w-full text-left rounded-lg border border-slate-200 hover:border-brand hover:bg-brand/5 p-3 transition-colors">
                  <div className="flex items-center gap-2"><I className="w-4 h-4 text-brand" /><span className="font-medium text-slate-800">{t.name}</span><span className="ml-auto text-xs text-slate-400">{t.duration_min} min</span></div>
                  {t.description ? <RichTextView html={t.description} className="text-xs text-slate-500 mt-1" /> : null}
                </button>); })}
            </div>
          ) : !slot ? (
            <div>
              {page.appointment_types.length > 1 ? <button onClick={() => setType(null)} className="text-sm text-slate-400 hover:text-slate-600 inline-flex items-center gap-1 mb-3"><ChevronLeft className="w-4 h-4" /> Types</button> : null}
              <div className="flex items-center justify-between mb-3">
                <button data-testid="prev-day" onClick={() => setDate(new Date(date.getTime() - 864e5))} className="p-2 rounded hover:bg-slate-100"><ChevronLeft className="w-4 h-4" /></button>
                <p className="font-semibold text-slate-700" data-testid="picked-date">{fmtDate(date)}</p>
                <button data-testid="next-day" onClick={() => setDate(new Date(date.getTime() + 864e5))} className="p-2 rounded hover:bg-slate-100"><ChevronLeft className="w-4 h-4 rotate-180" /></button>
              </div>
              <p className="text-xs text-slate-400 mb-3">Times shown in {TZ}</p>
              {slots === null ? <p className="text-sm text-slate-400 py-8 text-center">Loading times…</p>
                : slots.length === 0 ? <p className="text-sm text-slate-400 py-8 text-center" data-testid="no-slots">No available times this day. Try another date.</p>
                : <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="slot-grid">
                    {slots.map((s) => <button key={s.start} data-testid={`slot-${s.start}`} onClick={() => setSlot(s)}
                      className="rounded-lg border border-slate-200 hover:border-brand hover:bg-brand/5 py-2 text-sm font-medium text-slate-700">{s.label}</button>)}
                  </div>}
            </div>
          ) : (
            <div data-testid="booking-form">
              <button onClick={() => setSlot(null)} className="text-sm text-slate-400 hover:text-slate-600 inline-flex items-center gap-1 mb-3"><ChevronLeft className="w-4 h-4" /> Times</button>
              <p className="font-semibold text-slate-700">{type.name}</p>
              <p className="text-sm text-slate-500 mb-4">{new Date(slot.start).toLocaleString(undefined, { dateStyle: "full", timeStyle: "short" })} · {TZ}</p>
              <div className="grid grid-cols-2 gap-3">
                <Fld label="First Name*"><Input data-testid="bk-first" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></Fld>
                <Fld label="Last Name*"><Input data-testid="bk-last" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></Fld>
                <Fld label="Email*"><Input data-testid="bk-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Fld>
                <Fld label={`Mobile Phone${phoneRequired ? "*" : ""}`}><Input data-testid="bk-mobile" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} /></Fld>
              </div>
              {!interestHidden && (
                <div className="mt-3">
                  <Fld label={`Interested In${interestRequired ? "*" : ""}`}>
                    <Select value={form.interested_in} onValueChange={(v) => setForm({ ...form, interested_in: v })}>
                      <SelectTrigger data-testid="bk-interested"><SelectValue placeholder="Select an option" /></SelectTrigger>
                      <SelectContent className="bg-white">
                        {interestOptions.map((o) => <SelectItem key={o.code} value={o.code}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Fld>
                </div>
              )}
              <div className="mt-3 space-y-3">
                {(page.form?.optional_fields || []).includes("city") || (page.form?.optional_fields || []).includes("state") ? (
                  <div className="grid grid-cols-2 gap-3">
                    {(page.form?.optional_fields || []).includes("city") ? <Fld label="City"><Input data-testid="bk-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></Fld> : <div />}
                    {(page.form?.optional_fields || []).includes("state") ? <Fld label="State"><Input data-testid="bk-state" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} /></Fld> : null}
                  </div>
                ) : null}
                {(page.form?.optional_fields || []).includes("occupation") ? <Fld label="Current Occupation"><Input data-testid="bk-occupation" value={form.occupation} onChange={(e) => setForm({ ...form, occupation: e.target.value })} /></Fld> : null}
                {(page.form?.optional_fields || []).includes("comments") ? <Fld label="What would you like to discuss?"><Textarea data-testid="bk-comments" value={form.comments} onChange={(e) => setForm({ ...form, comments: e.target.value })} /></Fld> : null}
              </div>
              <input tabIndex={-1} autoComplete="off" value={form.company_website} onChange={(e) => setForm({ ...form, company_website: e.target.value })} className="hidden" aria-hidden="true" />
              <Button data-testid="bk-confirm" disabled={busy || !form.first_name || !form.last_name || !form.email || (phoneRequired && !form.mobile) || (interestRequired && !form.interested_in)}
                onClick={book} className="mt-4 w-full bg-brand hover:bg-brand-strong">{busy ? "Booking…" : "Confirm Booking"}</Button>
            </div>
          )}
        </div>
      </div>
    </Shell>
  );
}

function AuthBanner({ session }) {
  if (!session) return null;
  if (!session.authenticated) {
    return (
      <div className="mb-5 rounded-lg bg-[#0F2B5B]/5 border border-[#0F2B5B]/15 px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap" data-testid="recruiter-signin-prompt">
        <span className="text-sm text-slate-600">Are you a Top-Recruit recruiter booking on behalf of a candidate?</span>
        <a href={`/login?return=${encodeURIComponent(window.location.pathname + window.location.search)}`} className="text-sm font-semibold text-[#0F2B5B] hover:underline whitespace-nowrap" data-testid="recruiter-signin-link">Sign in to your account →</a>
      </div>
    );
  }
  if (session.role === "recruiter") {
    return (
      <div className="mb-5 rounded-lg bg-[#0F2B5B]/5 border border-[#0F2B5B]/15 px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap" data-testid="recruiter-identity-banner">
        <span className="text-sm text-slate-700">Booking as <b>{session.name}</b> · Recruiter</span>
        <Button size="sm" className="bg-[#0F2B5B] hover:opacity-90 text-white h-8" onClick={() => { window.location.href = session.page?.book_url || "/meetings/book"; }} data-testid="book-for-candidate-btn">
          <UserCog className="w-3.5 h-3.5 mr-1.5" />Book for a Candidate
        </Button>
      </div>
    );
  }
  if (session.role === "candidate") {
    return (
      <div className="mb-5 rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-2.5 flex items-center gap-2" data-testid="candidate-identity-banner">
        <span className="text-sm text-emerald-800">Booking as <b>{session.name}</b> — your details are pre-filled below.</span>
      </div>
    );
  }
  return null; // "other" authenticated users: no recruiter sign-in banner
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-[#0F2B5B] py-8 px-4" data-testid="public-schedule-shell">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-center mb-5">
          <img src="/assets/email/farmers-insurance-logo.png" alt="Farmers Insurance Career Center"
            className="h-16 w-auto bg-white rounded-lg px-4 py-2 shadow-sm" data-testid="public-brand-logo" />
        </div>
        <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-6 sm:p-8">{children}</div>
        <p className="text-center text-[11px] text-white/60 mt-5">Powered by Top-Recruit · Not an official Farmers Insurance corporate website</p>
      </div>
    </div>
  );
}
function Fld({ label, children }) { return <div><Label className="text-xs font-semibold text-slate-600 mb-1 block">{label}</Label>{children}</div>; }
function Row({ label, value }) { return <div className="flex gap-2"><span className="text-slate-400 w-20 shrink-0">{label}</span><span className="text-slate-700 font-medium">{value}</span></div>; }
